import {useCallback, useState} from 'react';

import type {User} from 'nutshell-core/users/users-types';

import type {Lead, WidgetFragment} from 'nutshell-graphql-types';

import {SummaryWidgetContent} from '../../../core-entity/widgets/summary-widget/summary-widget-content';

import {refetchForEvent} from '../../refetch-helpers';
import {LeadTagsEditor} from '../../shared/components/lead-tags-editor';
import {useGetLeadTags} from '../../shared/graphql/hooks';

import {useGetLeadSummary} from './graphql/hooks/use-get-lead-summary';
import {useUpdateLeadSummary} from './graphql/hooks/use-update-lead-summary';
import {LeadCloseDate} from './lead-close-date';
import {LeadConfidence} from './lead-confidence';
import {LeadOpenedBy} from './lead-opened-by';
import {LeadOpenedOn} from './lead-opened-on';
import {LeadOutcomeReason} from './lead-outcome-reason';
import {LeadTerritory} from './lead-territory';

interface Props {
    lead: Lead;
    widget: WidgetFragment;
    user: User;
}
export function LeadSummaryWidget(props: Props) {
    const [error, setError] = useState<string | null | undefined>(null);
    const {fetchMore: fetchMoreTags} = useGetLeadTags();

    const hasFullHeightSidebar = NutClientConfig.hasCoreEntityFullHeightSidebarFF;

    const {leadSummary, isLoading} = useGetLeadSummary(props.lead.id);
    const {updateLead} = useUpdateLeadSummary();

    const handleUpdateCreatedDate = useCallback(
        (createdDate?: Date | null) => {
            if (!leadSummary) {
                return;
            }

            updateLead(
                {
                    id: leadSummary.id,
                    createdTime: createdDate ? createdDate.getTime() / 1000 : null,
                },
                leadSummary
            ).catch((error) => {
                setError(error.message);
            });
        },
        [updateLead, leadSummary]
    );

    const handleUpdateDueDate = useCallback(
        (dueDate?: Date | null) => {
            if (!leadSummary) {
                return;
            }

            updateLead(
                {
                    id: leadSummary.id,
                    dueTime: dueDate ? dueDate.getTime() / 1000 : null,
                },
                leadSummary
            )
                .then(() => {
                    refetchForEvent('dueDateUpdated');
                })
                .catch((error) => {
                    setError(error.message);
                });
        },
        [updateLead, leadSummary]
    );

    const handleUpdateClosedDate = useCallback(
        (closedDate?: Date | null) => {
            if (!leadSummary) {
                return;
            }

            updateLead(
                {
                    id: leadSummary.id,
                    closedTime: closedDate ? closedDate.getTime() / 1000 : null,
                },
                leadSummary
            ).catch((error) => {
                setError(error.message);
            });
        },
        [updateLead, leadSummary]
    );

    const handleUpdateConfidence = useCallback(
        (confidence: number) => {
            if (!leadSummary) {
                return;
            }

            updateLead(
                {
                    id: leadSummary.id,
                    confidence,
                },
                leadSummary
            ).catch((error) => {
                setError(error.message);
            });
        },
        [updateLead, leadSummary]
    );

    return (
        <SummaryWidgetContent
            widget={props.widget}
            fields={
                <>
                    <LeadOpenedBy lead={leadSummary} />
                    <LeadOpenedOn
                        createdTime={leadSummary?.createdTime}
                        onUpdateLeadCreatedDate={handleUpdateCreatedDate}
                    />
                    <LeadCloseDate
                        lead={leadSummary}
                        onUpdateLeadDueDate={handleUpdateDueDate}
                        onUpdateLeadClosedDate={handleUpdateClosedDate}
                    />
                    <LeadConfidence
                        value={leadSummary?.confidence ?? 0}
                        onUpdate={handleUpdateConfidence}
                    />
                    {leadSummary?.territory && <LeadTerritory territory={leadSummary.territory} />}
                    {(leadSummary?.status === 'CANCELLED' || leadSummary?.status === 'LOST') &&
                        leadSummary?.outcome?.name && (
                            <LeadOutcomeReason
                                outcome={leadSummary.status}
                                reason={leadSummary.outcome.name}
                            />
                        )}
                </>
            }
            isLoading={isLoading}
            tags={
                hasFullHeightSidebar ? (
                    <LeadTagsEditor
                        lead={props.lead}
                        user={props.user}
                        fetchTags={fetchMoreTags}
                        maxItems={5}
                    />
                ) : null
            }
            error={error}
            setError={setError}
        />
    );
}
